import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import ReviewAlertBanner from "./components/ReviewAlertBanner";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import ScheduleCalendar from "./pages/ScheduleCalendar";
import EmployeeManagement from "./pages/EmployeeManagement";
import ShiftRequests from "./pages/ShiftRequests";
import EmployeeSchedule from "./pages/EmployeeSchedule";
import NotificationsPage from "./pages/NotificationsPage";
import ParticipantsPage from "./pages/ParticipantsPage";
import ImportPage from "./pages/ImportPage";
import PrintSchedule from "./pages/PrintSchedule";
import SettingsPage from "./pages/SettingsPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import StaffingPage from "./pages/StaffingPage";
import PoliciesPage from "./pages/PoliciesPage";
import CareersPage from "./pages/CareersPage";
import PropertyListingPage from "./pages/PropertyListingPage";
import CeoLifePage from "./pages/ceo-life/CeoLifePage";
import MeetingsPage from "./pages/MeetingsPage";
import FinancePage from "./pages/finance/FinancePage";
import BoardPage from "./pages/board/BoardPage";
import BookStudioPage from "./pages/book/BookStudioPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import CredentialsPage from "./pages/CredentialsPage";
import VolunteerApplyPage from "./pages/VolunteerApplyPage";
import VolunteersPage from "./pages/VolunteersPage";
import DonorsPage from "./pages/DonorsPage";
import DonorProfilePage from "./pages/DonorProfilePage";
import MarketingPage from "./pages/MarketingPage";
import DonatePage from "./pages/DonatePage";
import EventPage from "./pages/EventPage";
import EventLivePage from "./pages/EventLivePage";
import OptimizerPage from "./pages/optimizer/OptimizerPage";
import CaseloadsPage from "./pages/CaseloadsPage";
import PerformancePage from "./pages/perf/PerformancePage";
import MyReviewsPage from "./pages/MyReviewsPage";
import CommandPalette, { useCommandPalette } from "./components/CommandPalette";
import { useRealtime, useRealtimeEvent } from "./utils/realtime";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";
import logger from "./utils/logger";

const NOTIFICATION_POLL_INTERVAL_MS = 30000;

function AppLayout() {
  const { user, api } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useCommandPalette();

  // Realtime channel: open one connection per logged-in session and dispatch
  // each event as window CustomEvent (rt:shifts.changed, rt:meetings.changed, …).
  useRealtime(!!user);

  // Refresh notification badge instantly when something changes elsewhere.
  useRealtimeEvent(['shifts.changed', 'meetings.changed'], () => {
    fetchUnreadRef.current?.();
  });

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (err) {
      logger.error('Failed to fetch notifications:', err.message);
    }
  }, [api]);

  // Keep a ref to the latest fetchUnread so realtime events can call it
  // without recreating subscriptions on each render.
  const fetchUnreadRef = React.useRef(fetchUnread);
  React.useEffect(() => { fetchUnreadRef.current = fetchUnread; }, [fetchUnread]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, fetchUnread, location.pathname]);

  if (!user) return null;

  // Lock-in: new hires that haven't finished orientation are forced to /onboarding
  const needsOnboarding = user.requires_staff_onboarding && !user.staff_onboarding_complete;
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F9F8F6]" data-testid="app-layout">
      <Sidebar unreadCount={unreadCount} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <main className="flex-1 overflow-auto">
        <ReviewAlertBanner />
        <Routes>
          <Route path="/dashboard" element={<ProtectedRoute requireScheduler><AdminDashboard /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute requireScheduler><ScheduleCalendar /></ProtectedRoute>} />
          <Route path="/activities" element={<ProtectedRoute><ActivitiesPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute requireSupervisor><EmployeeManagement /></ProtectedRoute>} />
          <Route path="/participants" element={<ProtectedRoute requireSupervisor><ParticipantsPage /></ProtectedRoute>} />
          <Route path="/import" element={<ProtectedRoute requireScheduler><ImportPage /></ProtectedRoute>} />
          <Route path="/print" element={<ProtectedRoute><PrintSchedule /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requireAdmin><SettingsPage /></ProtectedRoute>} />
          <Route path="/optimizer" element={<ProtectedRoute requireAdmin><OptimizerPage /></ProtectedRoute>} />
          <Route path="/caseloads" element={<ProtectedRoute><CaseloadsPage /></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute requireAdmin><PerformancePage /></ProtectedRoute>} />
          <Route path="/my-reviews" element={<ProtectedRoute><MyReviewsPage /></ProtectedRoute>} />
          <Route path="/staffing" element={<ProtectedRoute requireAdmin><StaffingPage /></ProtectedRoute>} />
          <Route path="/shift-requests" element={<ProtectedRoute><ShiftRequests /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/my-schedule" element={<ProtectedRoute><EmployeeSchedule /></ProtectedRoute>} />
          <Route path="/policies" element={<ProtectedRoute><PoliciesPage /></ProtectedRoute>} />
          <Route path="/ceo-life" element={<ProtectedRoute><CeoLifePage /></ProtectedRoute>} />
          <Route path="/meetings" element={<ProtectedRoute><MeetingsPage /></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
          <Route path="/board" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
          <Route path="/book" element={<ProtectedRoute><BookStudioPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/credentials" element={<ProtectedRoute requireSupervisor><CredentialsPage /></ProtectedRoute>} />
          <Route path="/volunteers" element={<ProtectedRoute requireSupervisor><VolunteersPage /></ProtectedRoute>} />
          <Route path="/donors" element={<ProtectedRoute><DonorsPage /></ProtectedRoute>} />
          <Route path="/donors/:id" element={<ProtectedRoute><DonorProfilePage /></ProtectedRoute>} />
          <Route path="/marketing" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to={
            user.role === 'board_director' ? '/board'
            : user.role === 'hr' ? '/onboarding'
            : ['administrator','supervisor','scheduler'].includes(user.role) ? '/dashboard'
            : '/my-schedule'
          } replace />} />
        </Routes>
      </main>
      <InstallPrompt />
    </div>
  );
}

function AppRouter() {
  const { user, loading } = useAuth();

  // While auth is still being verified, render a neutral loading screen
  // instead of redirecting to /login. Otherwise a deep-link navigation
  // (e.g. /ceo-life?calendar_connected=1 after Google OAuth) gets bounced
  // through /login and lost when the user state arrives a moment later.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]" data-testid="auth-loading-screen">
        <div className="h-8 w-8 rounded-full border-2 border-[#6B3FA0] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/volunteer" element={<VolunteerApplyPage />} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/events/:slug" element={<EventPage />} />
      <Route path="/event-live/:slug" element={<ProtectedRoute><EventLivePage /></ProtectedRoute>} />
      <Route path="/eventadmin" element={<ProtectedRoute><EventLivePage /></ProtectedRoute>} />
      <Route path="/property/:slug" element={<PropertyListingPage />} />
      <Route path="/*" element={user ? <AppLayout /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
